import { Activity, LogOut, User, LifeBuoy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NotificationBell from "@/components/NotificationBell";
import MessageCenter from "@/components/MessageCenter";
import { useI18n } from "@/lib/i18n";

const PROJEXINO_LOGO = "https://customer-assets.emergentagent.com/job_healthcare-saas-hub/artifacts/lz1yk0gv_cropped-projexino-scaled-1-768x358%20%281%29.png";

export default function AppHeader({ title, subtitle, logoUrl, badge }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const isSuperAdmin = user?.role === "super_admin";
  const [openThreadId, setOpenThreadId] = useState(null);

  return (
    <header className="sticky top-0 z-40 bg-[#004a99] text-white shadow-md">
      <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-4 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-white ring-1 ring-white/30 flex items-center justify-center overflow-hidden p-1 flex-shrink-0" data-testid="app-logo">
            {logoUrl ? (
              <img src={logoUrl} alt="logo" className="h-full w-full object-cover rounded-md" />
            ) : isSuperAdmin ? (
              <img src={PROJEXINO_LOGO} alt="Projexino" className="h-full w-full object-contain" />
            ) : (
              <Activity className="h-5 w-5 text-[#004a99]" />
            )}
          </div>
          <div className="leading-tight min-w-0">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.22em] text-white/70 font-semibold">Pulse-Agent AI</div>
            <div className="text-base sm:text-lg font-heading font-semibold truncate max-w-[180px] sm:max-w-none" data-testid="app-title">{title}</div>
          </div>
        </div>

        {badge && (
          <span className="ml-1 sm:ml-3 hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-white/90 bg-white/10 ring-1 ring-white/20 rounded-full px-3 py-1">
            <span className="pulse-dot" /> {badge}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1.5 sm:gap-3 flex-wrap justify-end">
          <LanguageSwitcher inverse />
          {subtitle && <span className="hidden xl:block text-sm text-white/80">{subtitle}</span>}
          {user && (
            <>
              <div className="text-right hidden md:block">
                <div className="text-sm font-medium" data-testid="user-name">{user.name}</div>
                <div className="text-xs text-white/70 truncate max-w-[160px]">{user.email}</div>
              </div>
              {user.role !== "super_admin" && user.role !== "patient" && (
                <>
                  <Button variant="secondary" size="sm" className="bg-white/15 hover:bg-white/25 text-white border-0 icon-wiggle px-2 sm:px-3"
                    data-testid="header-profile-btn" onClick={() => navigate("/profile")} title="Profile & signature">
                    <User className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Profile</span>
                  </Button>
                  <Button variant="secondary" size="sm" className="bg-white/15 hover:bg-white/25 text-white border-0 icon-wiggle px-2 sm:px-3"
                    data-testid="header-distribution-btn" onClick={() => navigate("/distribution-kit")} title="WhatsApp distribution kit">
                    <Share2 className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Distribute</span>
                  </Button>
                </>
              )}
              <Button variant="secondary" size="sm" className="bg-white/15 hover:bg-white/25 text-white border-0 px-2 sm:px-3"
                data-testid="header-help-btn" onClick={() => navigate("/help")} title="Help & support">
                <LifeBuoy className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Help</span>
              </Button>
              <NotificationBell onOpenInbox={(tid) => setOpenThreadId(tid)} />
              <Button
                variant="secondary"
                size="sm"
                className="bg-white/15 hover:bg-white/25 text-white border-0 px-2 sm:px-3"
                data-testid="logout-button"
                onClick={() => { logout(); navigate("/login"); }}
              >
                <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">{t("nav_logout")}</span>
              </Button>
            </>
          )}
        </div>
      </div>
      {user && <MessageCenter openThreadId={openThreadId} onConsumeOpen={() => setOpenThreadId(null)} />}
    </header>
  );
}
